/**
 * Native guard around the core-js floor polyfills (vingreact#981, plan
 * invariant I6: a current browser keeps every native it ships).
 *
 * core-js replaces a built-in when the engine's copy fails one of its
 * conformance checks. For a few modules the floor needs, current engines fail
 * such a check too (see CURRENT_ENGINE_MODULES in scripts/floor-polyfills.mjs):
 * importing core-js would swap Chrome's own `structuredClone`, Safari's
 * `Iterator.prototype.take`, … for a JavaScript copy on every up-to-date device.
 *
 * So `floorPolyfills.generated.ts` imports this module FIRST (it records what
 * exists before core-js runs) and `floorNativeRestore.ts` LAST (it puts each
 * recorded native back). The result is missing-only: an engine without the API
 * (the floor) keeps core-js's copy; an engine with it keeps its own.
 *
 * `scripts/floor-polyfills.mjs` derives GUARDED_NATIVES (see below).
 */
import { GUARDED_NATIVES } from '@/lib/floorGuardedNatives.generated';

/**
 * Dotted paths from `globalThis`: DERIVED by scripts/floor-polyfills.mjs
 * (`guardedNativesFor`) into floorGuardedNatives.generated.ts, never hand-kept.
 * It holds every native a core-js module the polyfills load replaces on a
 * current engine: named by core-js-compat's data (CURRENT_ENGINE_MODULES), or
 * seen replaced when the module ran alone on the pinned current engine
 * (`npm run floor:sweep`, scripts/floor-boot/current-engine-natives.json). The
 * sweep is what finds the runtime cases compat data cannot: Chromium 143
 * failing core-js 3.50's `Uint8Array.prototype.setFromHex` check (#981 981c),
 * `DOMException.prototype.constructor` (web.dom-exception.stack re-points it at
 * its wrapper) and `Function.prototype.toString` (core-js's make-built-in
 * replaces it on EVERY engine so its functions print `[native code]`).
 */
export { GUARDED_NATIVES };

export interface Snapshot {
  owner: object;
  key: PropertyKey;
  descriptor: PropertyDescriptor;
}

/** An own `undefined` value is a missing API (src/test/simulateFloor.ts). */
function present(descriptor: PropertyDescriptor | undefined): descriptor is PropertyDescriptor {
  return !!descriptor && (!!descriptor.get || descriptor.value !== undefined);
}

function ownerOf(path: string): { owner: object; key: string } | null {
  const parts = path.split('.');
  const key = parts.pop();
  if (key === undefined) return null;
  let owner: unknown = globalThis;
  for (const part of parts) {
    if (owner === null || (typeof owner !== 'object' && typeof owner !== 'function')) return null;
    owner = (owner as Record<string, unknown>)[part];
  }
  if (owner === null || (typeof owner !== 'object' && typeof owner !== 'function')) return null;
  return { owner, key };
}

/** Descriptors of every guarded native that exists right now. */
export function snapshotGuardedNatives(paths: readonly string[] = GUARDED_NATIVES): Snapshot[] {
  const out: Snapshot[] = [];
  for (const path of paths) {
    const at = ownerOf(path);
    if (!at) continue;
    const descriptor = Object.getOwnPropertyDescriptor(at.owner, at.key);
    // An own `undefined` value is a missing API (how the tests simulate the
    // floor, src/test/simulateFloor.ts): core-js's copy must stay.
    if (present(descriptor)) out.push({ owner: at.owner, key: at.key, descriptor });
  }
  return out;
}

/**
 * Every own property (string and symbol keys) of each owner, as it is right now
 * (#981 981b). Used around a dependency that bundles its own core-js (pdf.js's
 * `legacy/` build): whatever it replaces is put back with `restoreGuardedNatives`,
 * so each API keeps ONE live implementation, the engine's or the app's pinned
 * core-js; properties it adds that nothing had are left in place.
 */
export function snapshotOwnProperties(owners: readonly unknown[]): Snapshot[] {
  const out: Snapshot[] = [];
  const seen = new Set<unknown>();
  for (const owner of owners) {
    if (owner === null || (typeof owner !== 'object' && typeof owner !== 'function') || seen.has(owner)) continue;
    seen.add(owner);
    for (const key of Reflect.ownKeys(owner)) {
      const descriptor = Object.getOwnPropertyDescriptor(owner, key);
      if (present(descriptor)) out.push({ owner, key, descriptor });
    }
  }
  return out;
}

/** Put every snapshotted native back. Returns the paths that had been replaced. */
export function restoreGuardedNatives(snapshot: readonly Snapshot[]): string[] {
  const restored: string[] = [];
  for (const { owner, key, descriptor } of snapshot) {
    const now = Object.getOwnPropertyDescriptor(owner, key);
    if (now && Object.is(now.value, descriptor.value) && now.get === descriptor.get && now.set === descriptor.set) continue;
    Object.defineProperty(owner, key, descriptor);
    restored.push(String(key));
  }
  return restored;
}

/** Taken when this module evaluates: before any core-js module (import order). */
export const BEFORE_CORE_JS: readonly Snapshot[] = snapshotGuardedNatives();
